import { useNavigate } from "react-router-dom";

import {
  Build as AutomationIcon,
  Security as SecurityIcon,
  GitHub as GitHubIcon,
  Visibility as ObservabilityIcon,
  Key as KeyIcon,
  KeyboardArrowRight as ArrowIcon,
  AccountTree as ArchitectureIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import InterestForm from "../components/interestForm";
import { tokens } from "../theme";

const eyebrowSx = {
  fontFamily: "'IBM Plex Mono', ui-monospace, Menlo, monospace",
  fontSize: 12,
  fontWeight: 500,
  letterSpacing: "0.12em",
  textTransform: "uppercase" as const,
  color: "text.secondary",
};

const codeSx = {
  fontFamily: "'IBM Plex Mono', ui-monospace, Menlo, monospace",
  fontSize: 13,
};

export function Home() {
  const navigate = useNavigate();

  const features = [
    {
      title: "One-click Kubernetes",
      icon: <ArchitectureIcon fontSize="medium" />,
      description:
        "Production-ready clusters on AWS in minutes. Automated lifecycle, multi-AZ, upgrades that don't page you at 3am.",
    },
    {
      title: "Integrated DevOps suite",
      icon: <GitHubIcon fontSize="medium" />,
      description:
        "GitLab, Harbor, Argo CD — pre-installed, wired together, and kept on the latest stable versions.",
    },
    {
      title: "Enterprise security",
      icon: <SecurityIcon fontSize="medium" />,
      description:
        "OIDC, RBAC, cert-manager, OpenBao secrets, and Keycloak SSO built in. You don't have to wire them up.",
    },
    {
      title: "GitOps ready",
      icon: <AutomationIcon fontSize="medium" />,
      description:
        "Helm, Kustomize, raw manifests — whatever you prefer. Argo CD drives declarative deploys.",
    },
    {
      title: "Full observability",
      icon: <ObservabilityIcon fontSize="medium" />,
      description:
        "OpenTelemetry, Prometheus, Grafana. Metrics, logs, and traces stitched together on day one.",
    },
    {
      title: "Usage-based billing",
      icon: <KeyIcon fontSize="medium" />,
      description:
        "Per-minute pricing, no upfront commitment. Spin a cluster up for an afternoon; shut it down when you're done.",
    },
  ];

  const technologies = [
    "Kubernetes",
    "GitLab",
    "Harbor",
    "Argo CD",
    "OpenBao",
    "Keycloak",
    "cert-manager",
    "OpenTelemetry",
    "Prometheus",
    "Grafana",
    "Headscale",
    "kops",
  ];

  return (
    <Box sx={{ overflow: "hidden" }}>
      {/* Hero */}
      <Container maxWidth="lg" sx={{ pt: { xs: 6, md: 10 }, pb: { xs: 6, md: 10 } }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={7}>
            <Stack spacing={3}>
              <Typography sx={eyebrowSx}>· open cloud, on demand</Typography>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2.5rem", sm: "3.25rem", md: "4rem" },
                  color: "text.primary",
                  textWrap: "balance",
                  m: 0,
                }}
              >
                Cloud-native infrastructure,{" "}
                <Box component="span" sx={{ color: "secondary.main" }}>
                  made simple
                </Box>
                .
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{
                  color: "text.secondary",
                  fontSize: { xs: "1.05rem", md: "1.125rem" },
                  lineHeight: 1.55,
                  maxWidth: 600,
                }}
              >
                One-click Kubernetes on AWS with GitLab, Harbor, Argo CD, and the
                rest of the open-source toolchain — pre-configured and kept
                running so you can focus on your product.
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ pt: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  endIcon={<ArrowIcon />}
                  onClick={() => navigate("/signup")}
                  sx={{ px: 3, py: 1.25, borderRadius: 0.5, fontSize: 15 }}
                >
                  Get started
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  onClick={() => navigate("/pricing")}
                  sx={{
                    px: 3,
                    py: 1.25,
                    borderRadius: 0.5,
                    fontSize: 15,
                    borderColor: "divider",
                    color: "text.primary",
                    "&:hover": {
                      backgroundColor: "var(--bg-sunken)",
                      borderColor: "primary.main",
                    },
                  }}
                >
                  View pricing
                </Button>
              </Stack>
              <Typography sx={{ ...codeSx, color: "text.secondary", pt: 1 }}>
                $ <Box component="span" sx={{ color: tokens.term[600] }}>mu</Box> cluster up ·
                ready in ~5 min
              </Typography>
            </Stack>
          </Grid>

          <Grid item xs={12} md={5} sx={{ display: { xs: "none", md: "block" } }}>
            <Paper
              elevation={0}
              sx={{
                background: `linear-gradient(180deg, ${tokens.night[900]} 0%, ${tokens.night[700]} 100%)`,
                color: tokens.paper[100],
                borderRadius: 3,
                p: 3,
                border: "1px solid rgba(135, 189, 224, 0.12)",
                boxShadow: "0 6px 16px rgba(0,0,0,0.25), 0 12px 28px rgba(0,0,0,0.25)",
              }}
            >
              <Typography
                sx={{
                  ...eyebrowSx,
                  color: tokens.sky[300],
                }}
              >
                services · acme-platform · prod
              </Typography>
              <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.25 }}>
                {[
                  { n: "kubernetes", v: "1.29.3", s: "running", c: tokens.term[500] },
                  { n: "vault", v: "1.15.6", s: "running", c: tokens.term[500] },
                  { n: "gitlab", v: "16.9.1", s: "updating", c: tokens.sky[400] },
                  { n: "keycloak", v: "24.0.2", s: "running", c: tokens.term[500] },
                  { n: "prometheus", v: "2.51.0", s: "running", c: tokens.term[500] },
                  { n: "backstage", v: "1.24.0", s: "degraded", c: tokens.amber[500] },
                ].map((svc) => (
                  <Box
                    key={svc.n}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr auto auto",
                      alignItems: "center",
                      gap: 1.5,
                      ...codeSx,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: svc.c }} />
                      <Box sx={{ color: tokens.paper[100] }}>{svc.n}</Box>
                    </Box>
                    <Box sx={{ color: tokens.night[200], fontSize: 12 }}>{svc.v}</Box>
                    <Box sx={{ color: svc.c, fontSize: 12 }}>{svc.s}</Box>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Stats */}
      <Box sx={{ borderTop: "1px solid", borderBottom: "1px solid", borderColor: "divider", backgroundColor: "var(--bg-sunken)" }}>
        <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 } }}>
          <Grid container spacing={{ xs: 3, md: 4 }}>
            {[
              { value: "~5 min", label: "Cluster setup" },
              { value: "10+", label: "Integrated tools" },
              { value: "$0", label: "Upfront cost" },
              { value: "24/7", label: "Keep-it-running" },
            ].map((stat) => (
              <Grid item xs={6} md={3} key={stat.label}>
                <Stack spacing={1}>
                  <Typography sx={eyebrowSx}>{stat.label}</Typography>
                  <Typography
                    sx={{
                      fontFamily: "'IBM Plex Serif', ui-serif, Georgia, serif",
                      fontSize: { xs: "2rem", md: "2.5rem" },
                      fontWeight: 500,
                      letterSpacing: "-0.02em",
                      color: "primary.dark",
                      lineHeight: 1.1,
                    }}
                  >
                    {stat.value}
                  </Typography>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Features */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Stack spacing={2} sx={{ mb: 6, maxWidth: 720 }}>
          <Typography sx={eyebrowSx}>why microcumulus</Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "2rem", md: "2.75rem" },
              color: "text.primary",
            }}
          >
            Best-in-class open source, kept running for you.
          </Typography>
          <Typography variant="subtitle1" sx={{ color: "text.secondary", fontSize: "1.05rem" }}>
            We deploy the best open-source tools available, keep them up to date,
            and teach you how to do the same. No lock-in, no proprietary layers
            you'll regret later.
          </Typography>
        </Stack>

        <Grid container spacing={3}>
          {features.map((feature) => (
            <Grid item xs={12} sm={6} md={4} key={feature.title}>
              <Paper
                elevation={0}
                sx={{
                  p: 3.5,
                  height: "100%",
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "background.paper",
                  transition: "box-shadow 200ms cubic-bezier(.2,.8,.2,1), border-color 200ms cubic-bezier(.2,.8,.2,1)",
                  "&:hover": {
                    borderColor: "var(--border-2)",
                    boxShadow: "0 2px 4px rgba(16,26,47,0.06), 0 4px 12px rgba(16,26,47,0.06)",
                  },
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 0.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "var(--accent-soft)",
                    color: "secondary.dark",
                    mb: 2,
                  }}
                >
                  {feature.icon}
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "1.125rem",
                    fontWeight: 600,
                    color: "text.primary",
                    mb: 1,
                  }}
                >
                  {feature.title}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.55 }}>
                  {feature.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Technologies */}
      <Box
        sx={{
          background: `linear-gradient(180deg, ${tokens.night[900]} 0%, ${tokens.night[700]} 100%)`,
          color: tokens.paper[100],
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Stack spacing={2} sx={{ mb: 5, maxWidth: 720 }}>
            <Typography sx={{ ...eyebrowSx, color: tokens.sky[300] }}>stack</Typography>
            <Typography
              variant="h3"
              sx={{
                fontFamily: "'IBM Plex Serif', ui-serif, Georgia, serif",
                fontWeight: 500,
                fontSize: { xs: "1.75rem", md: "2.5rem" },
                letterSpacing: "-0.02em",
                color: tokens.paper[50],
              }}
            >
              Built on the software the industry already trusts.
            </Typography>
            <Typography sx={{ color: tokens.night[200], fontSize: "1rem" }}>
              We don't reinvent. We pick the tools experienced platform teams
              already reach for, and keep the seams between them tight.
            </Typography>
          </Stack>

          <Stack direction="row" flexWrap="wrap" gap={1}>
            {technologies.map((tech) => (
              <Box
                key={tech}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  px: 1.5,
                  py: 0.75,
                  border: "1px solid",
                  borderColor: "rgba(135, 189, 224, 0.25)",
                  borderRadius: 999,
                  backgroundColor: "rgba(135, 189, 224, 0.06)",
                  fontFamily: "'IBM Plex Mono', ui-monospace, Menlo, monospace",
                  fontSize: 13,
                  color: tokens.paper[100],
                }}
              >
                {tech}
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* CTA + interest form */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 4, md: 5 },
                height: "100%",
                borderRadius: 1.75,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.paper",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Stack spacing={2.5}>
                <Typography sx={eyebrowSx}>ready when you are</Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontFamily: "'IBM Plex Serif', ui-serif, Georgia, serif",
                    fontWeight: 500,
                    fontSize: { xs: "1.75rem", md: "2.25rem" },
                    letterSpacing: "-0.02em",
                    color: "text.primary",
                  }}
                >
                  Deploy your first Kubernetes cluster.
                </Typography>
                <Typography sx={{ color: "text.secondary", fontSize: "1rem", lineHeight: 1.55 }}>
                  Production-ready clusters with the full toolchain already wired
                  in. Per-minute billing, no upfront commitment. Tear it down when
                  you're done — or keep it running forever; it's yours.
                </Typography>
                <Box>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    endIcon={<ArrowIcon />}
                    onClick={() => navigate("/signup")}
                    sx={{ px: 3, py: 1.25, borderRadius: 0.5, fontSize: 15 }}
                  >
                    Get started
                  </Button>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <InterestForm />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
